export interface HighlightItem {
  icon: string;
  title: string;
  text: string;
}

export interface VehicleDescriptionStructured {
  intro_paragraph: string;
  highlights: HighlightItem[];
  closing_paragraph: string;
  fallback: boolean;
}

export interface VehicleFormSnapshot {
  year?: number;
  make?: string;
  model?: string;
  trim?: string;
  mileage?: number;
  engine?: string;
  transmission?: string;
  fuel_type?: string;
  drivetrain?: string;
  exterior_color?: string;
  interior_color?: string;
  body_style?: string;
  asking_price?: number | null;
  bhph_eligible?: boolean;
  bhph_min_down_type?: string | null;
  bhph_min_down_value?: number | null;
  bhph_payment_amount?: number | null;
  bhph_payment_frequency?: string | null;
  bhph_term_months?: number | null;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function buildSpecsGrid(v: VehicleFormSnapshot): string {
  const rows: string[] = [];
  const fuelEconomy =
    v.make?.toLowerCase().includes('toyota') || v.make?.toLowerCase().includes('honda')
      ? '28 City / 36 Hwy MPG'
      : '22 City / 29 Hwy MPG';
  const seating =
    v.body_style?.toLowerCase().includes('suv') || v.body_style?.toLowerCase().includes('crossover')
      ? '5-7 Passengers'
      : v.body_style?.toLowerCase().includes('sedan')
        ? '5 Passengers'
        : '5-8 Passengers';

  if (v.engine) rows.push(`<div><strong>Engine:</strong> ${escapeHtml(v.engine)}</div>`);
  if (v.transmission)
    rows.push(`<div><strong>Transmission:</strong> ${escapeHtml(v.transmission)}</div>`);
  if (v.fuel_type) rows.push(`<div><strong>Fuel Type:</strong> ${escapeHtml(v.fuel_type)}</div>`);
  if (v.drivetrain)
    rows.push(`<div><strong>Drivetrain:</strong> ${escapeHtml(v.drivetrain)}</div>`);
  rows.push(`<div><strong>Fuel Economy:</strong> ${fuelEconomy}</div>`);
  rows.push(`<div><strong>Seating:</strong> ${seating}</div>`);
  if (v.mileage)
    rows.push(`<div><strong>Mileage:</strong> ${v.mileage.toLocaleString()} miles</div>`);
  if (v.exterior_color)
    rows.push(`<div><strong>Exterior:</strong> ${escapeHtml(v.exterior_color)}</div>`);
  if (v.interior_color)
    rows.push(`<div><strong>Interior:</strong> ${escapeHtml(v.interior_color)}</div>`);

  return `<div style="background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%); padding: 16px; border-radius: 8px; margin-bottom: 16px;">
  <h4 style="color: #1e40af; margin-bottom: 8px; font-size: 14px; font-weight: 600;">🚗 VEHICLE SPECIFICATIONS</h4>
  <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 8px; font-size: 13px;">
    ${rows.join('\n    ')}
  </div>
</div>`;
}

function buildPricingSection(v: VehicleFormSnapshot): string {
  if (v.bhph_eligible && (v.bhph_payment_amount || v.bhph_min_down_value)) {
    const freqLabel = v.bhph_payment_frequency === 'biweekly' ? 'Bi-Weekly' : 'Monthly';
    const downDisplay =
      v.bhph_min_down_type === 'percent' && v.asking_price && v.bhph_min_down_value
        ? `$${(v.asking_price * (v.bhph_min_down_value / 100)).toLocaleString()} (${v.bhph_min_down_value}%)`
        : v.bhph_min_down_value
          ? `$${v.bhph_min_down_value.toLocaleString()}`
          : null;

    const items: string[] = [];
    if (downDisplay) items.push(`<li><strong>Min Down Payment:</strong> ${downDisplay}</li>`);
    if (v.bhph_payment_amount)
      items.push(
        `<li><strong>Payment:</strong> $${v.bhph_payment_amount.toLocaleString()}/${freqLabel}</li>`
      );
    if (v.bhph_term_months)
      items.push(`<li><strong>Term:</strong> ${v.bhph_term_months} months</li>`);

    return `<div style="background: #f0fdf4; padding: 12px; border-radius: 6px; margin-bottom: 16px; border-left: 4px solid #16a34a;">
  <p style="margin: 0 0 6px 0; color: #14532d; font-size: 14px;"><strong>💰 Asking Price:</strong> ${v.asking_price ? `$${v.asking_price.toLocaleString()}` : 'Call for pricing'}</p>
  <p style="margin: 0 0 4px 0; color: #14532d; font-size: 14px; font-weight: 600;">🏦 In-House BHPH Financing Available:</p>
  <ul style="margin: 4px 0 0 16px; padding: 0; color: #166534; font-size: 13px;">
    ${items.join('\n    ')}
  </ul>
</div>`;
  }

  return `<div style="background: #f0f9ff; padding: 12px; border-radius: 6px; margin-bottom: 16px; border-left: 4px solid #0ea5e9;">
  <p style="margin: 0; color: #0c4a6e; font-size: 14px;">
    <strong>💰 Pricing:</strong> ${v.asking_price ? `$${v.asking_price.toLocaleString()}` : 'Call for pricing'}
    ${v.asking_price ? ' — Financing options available with competitive rates' : ''}
  </p>
</div>`;
}

export function buildDescriptionHtml(
  structured: VehicleDescriptionStructured,
  vehicleData: VehicleFormSnapshot,
  warrantyContent: string
): string {
  const vehicleName = [vehicleData.year, vehicleData.make, vehicleData.model, vehicleData.trim]
    .filter(Boolean)
    .join(' ');

  const highlightItems = structured.highlights
    .map((h) => `<li>${h.icon} <strong>${escapeHtml(h.title)}:</strong> ${escapeHtml(h.text)}</li>`)
    .join('\n          ');

  return `<h3 style="color: #1f2937; margin-bottom: 12px;">${escapeHtml(vehicleName)}</h3>

${buildSpecsGrid(vehicleData)}

<div style="margin-bottom: 16px;">
  <p style="margin-bottom: 12px;">${escapeHtml(structured.intro_paragraph)}</p>
  <p style="margin-bottom: 0;">${escapeHtml(structured.closing_paragraph)}</p>
</div>

<p style="margin-bottom: 8px;"><strong>✨ Key Highlights:</strong></p>
<ul style="margin-bottom: 16px; padding-left: 20px;">
          ${highlightItems}
</ul>

${buildPricingSection(vehicleData)}

<div style="background: #ecfdf5; padding: 12px; border-radius: 6px; margin-bottom: 16px;">
  <p style="margin: 0; color: #166534; font-size: 14px; font-weight: 600;">
    🎯 Ready for Immediate Delivery • 💳 Flexible Financing Available • 📞 Call Today for Test Drive
  </p>
</div>

<div style="background: #fefce8; padding: 12px; border-radius: 6px; border-left: 4px solid #ca8a04; margin-bottom: 8px;">
  <p style="margin: 0; color: #713f12; font-size: 13px;">
    <strong>⚖️ Warranty:</strong> ${escapeHtml(warrantyContent)}
  </p>
</div>`;
}
